import React, { useEffect } from "react";
import useSeo from "utils/useSeo";
import { pageDescription } from "utils/routeMeta";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Divider,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import GroupsIcon from "@mui/icons-material/Groups";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import PublicIcon from "@mui/icons-material/Public";
import BoltIcon from "@mui/icons-material/Bolt";
import { SubmitResumeDialog } from "components/common/dialogs";

const JobCard = ({ title, subheader, summary, bullets, compensation, tags, onApply }) => {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        backgroundColor: "#FFFFFF",
        border: "1px solid",
        borderColor: "divider",
        borderTop: "4px solid",
        borderTopColor: "primary.main",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.02)",
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: "0 10px 25px rgba(0, 146, 139, 0.1)",
          transform: "translateY(-6px)",
        },
      }}
    >
      <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Header */}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "dark.main", mb: 0.75, fontSize: "1.1rem" }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "text.secondary" }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 14 }} />
              <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>{subheader.split('|')[1].trim()}</Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.5 }}>•</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WorkIcon sx={{ fontSize: 14 }} />
              <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>{subheader.split('|')[0].trim()}</Typography>
            </Box>
          </Stack>
        </Box>

        {/* Summary */}
        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6, fontSize: "0.8125rem" }}>
          {summary}
        </Typography>

        {/* Bullet points */}
        <Box component="ul" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {bullets.map((item, i) => (
            <Box
              key={i}
              component="li"
              sx={{
                color: "text.secondary",
                fontSize: "0.8125rem",
                lineHeight: 1.55,
                "&::marker": { color: "primary.main" },
              }}
            >
              {item}
            </Box>
          ))}
        </Box>

        {/* Compensation */}
        <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 600, fontSize: "0.8rem" }}>
          {compensation}
        </Typography>

        {/* Tags */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {tags && tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                borderRadius: "6px",
                fontWeight: 600,
                fontSize: "0.65rem",
                height: "20px",
                backgroundColor: "primary.extraLight",
                color: "primary.main",
              }}
            />
          ))}
        </Stack>
      </CardContent>

      <Divider />

      <Box sx={{ p: 2, backgroundColor: "grey.50" }}>
        <Button
          onClick={() => onApply(title)}
          variant="contained"
          fullWidth
          startIcon={<EmailIcon />}
          sx={{
            backgroundColor: "primary.main",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: "primary.extraDark" },
          }}
        >
          Apply
        </Button>
      </Box>
    </Card>
  );
};

const ValueItem = ({ icon, title, description }) => (
  <Box sx={{ textAlign: "center", p: 1.5 }}>
    <Avatar
      sx={{
        width: 48,
        height: 48,
        mx: "auto",
        mb: 1.5,
        backgroundColor: "primary.extraLight",
        color: "primary.main",
        boxShadow: "0 8px 16px rgba(0, 146, 139, 0.1)",
        "& svg": { fontSize: 20 }
      }}
    >
      {icon}
    </Avatar>
    <Typography variant="h6" sx={{ fontWeight: 800, color: "dark.main", mb: 0.75, fontSize: "1rem" }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5, fontSize: "0.8125rem" }}>
      {description}
    </Typography>
  </Box>
);

const Careers = () => {
  useSeo({
    title: "Careers",
    description: pageDescription('/careers'),
    canonicalPath: "/careers",
  });
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedJobTitle, setSelectedJobTitle] = React.useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleApply = (title) => {
    setSelectedJobTitle(title);
    setDialogOpen(true);
  };

  const jobs = [
    {
      title: "Data Scientist",
      subheader: "Full-time | Remote",
      summary: "Design and improve vehicle routing algorithms at the intersection of combinatorial optimization and machine learning.",
      bullets: [
        "Own solver performance benchmarks and continuously improve route quality",
        "Analyze real-world fleet data to uncover efficiency opportunities",
        "Collaborate with engineers to ship production-grade optimization features",
        "3+ years in operations research; OR-Tools or Gurobi experience required",
      ],
      compensation: "Competitive salary + equity",
      tags: ["Optimization", "Machine Learning", "Python", "Data Analysis"],
    },
    {
      title: "Front-end Developer",
      subheader: "Full-time | Remote",
      summary: "Build the interfaces that make complex routing data intuitive — from the model builder to customer dashboards.",
      bullets: [
        "Own the model-builder UI and customer-facing dashboard end-to-end",
        "Translate routing data into clear, interactive visualizations",
        "Ship accessible, responsive components with high attention to detail",
        "3+ years with React & TypeScript; MUI and Recharts/D3 a strong plus",
      ],
      compensation: "Competitive salary + equity",
      tags: ["React", "Material-UI", "TypeScript", "UX/UI"],
    },
    {
      title: "Back-end Developer",
      subheader: "Full-time | Remote",
      summary: "Build and scale the APIs and infrastructure behind every solve.",
      bullets: [
        "Design the REST endpoints customers build against",
        "Optimize database queries and harden the platform so solves stay fast and reliable under load",
        "Collaborate with data scientists to integrate solver outputs into the API",
        "3+ years with Python (Django or FastAPI) and PostgreSQL; AWS/GCP a plus",
      ],
      compensation: "Competitive salary + equity",
      tags: ["Python", "Django", "PostgreSQL", "REST API"],
    },
  ];

  // These replaced four generic startup values ("High Impact", "Remote First",
  // "Collaborative Culture", "Rapid Growth") that could have sat on any careers
  // page and claimed things we can't back — "global fleets", "sustainability".
  // Each one below is specific enough that a candidate could check it.
  const values = [
    {
      icon: <RocketLaunchIcon />,
      title: "Real problems, real constraints",
      description: "Vehicle routing is NP-hard and the constraints come from actual dispatchers — time windows, capacities, shift hours. The hard part is making that usable.",
    },
    {
      icon: <GroupsIcon />,
      title: "A small team",
      description: "Small enough that you'll own whole areas rather than tickets, and see what you ship reach customers in days.",
    },
    {
      icon: <BoltIcon />,
      title: "Ship, then learn",
      description: "We put work in front of operators early and change it when they tell us it's wrong. Opinions are cheap; a route sheet a driver can follow is not.",
    },
    {
      icon: <PublicIcon />,
      title: "Remote, honestly",
      description: "Work from anywhere. We write things down, because a team spread across time zones can't rely on hallway conversations.",
    },
  ];

  return (
    <Box sx={{ backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 3, md: 6 },
          pb: { xs: 4, md: 6 },
          background: "linear-gradient(180deg, rgba(230, 252, 251, 0.5) 0%, #FFFFFF 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.25,
              borderRadius: "20px",
              backgroundColor: "primary.extraLight",
              color: "primary.main",
              mb: 2,
            }}
          >
            <WorkIcon sx={{ fontSize: 14 }} />
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 1 }}>
              Careers
            </Typography>
          </Box>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "1.75rem", md: "2.5rem" },
              fontWeight: 800,
              color: "dark.main",
              lineHeight: 1.1,
              mb: 2,
            }}
          >
            Come build <Box component="span" sx={{ color: "primary.main" }}>something operators trust</Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1rem",
              color: "text.secondary",
              maxWidth: "600px",
              mx: "auto",
              mb: 3,
              lineHeight: 1.5,
            }}
          >
            Join us in building route optimization that dispatchers and owner-operators can actually use. We're looking for people who like hard problems and plain interfaces.
          </Typography>
          <Button
            variant="contained"
            size="medium"
            onClick={() => document.getElementById('open-positions')?.scrollIntoView({ behavior: 'smooth' })}
            sx={{
              backgroundColor: "primary.main",
              px: 3,
              py: 1,
              fontSize: "0.875rem",
              fontWeight: 700,
              "&:hover": { backgroundColor: "primary.extraDark" },
            }}
          >
            See open roles
          </Button>
        </Container>
      </Box>

      {/* Why Join Us Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, color: "dark.main", mb: 1.5, fontSize: "1.75rem" }}>
            Why work here
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: "600px", mx: "auto", fontSize: "0.9rem" }}>
            No mission statement. Here's what the work is actually like.
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {values.map((value, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <ValueItem {...value} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Open roles Section */}
      <Box id="open-positions" sx={{ py: 6, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography variant="h2" sx={{ fontWeight: 800, color: "dark.main", mb: 0.5, fontSize: "1.75rem" }}>
                Open roles
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
                Three roles open. If none fit, the last card still applies.
              </Typography>
            </Box>
            <Chip
              label={`${jobs.length} open`}
              color="primary"
              size="small"
              sx={{ fontWeight: 700, borderRadius: "6px" }}
            />
          </Box>
          <Grid container spacing={3}>
            {jobs.map((job, index) => (
              <Grid item xs={12} md={4} key={index}>
                <JobCard {...job} onApply={handleApply} />
              </Grid>
            ))}
          </Grid>

          {/* Send a general application */}
          <Box
            sx={{
              mt: 6,
              p: 3,
              borderRadius: 3,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.extraLight} 0%, #FFFFFF 100%)`,
              border: "1px solid",
              borderColor: "primary.light",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800, color: "dark.main", mb: 0.5, fontSize: "1.1rem" }}>
              None of these quite you?
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 2.5, fontSize: "0.9rem" }}>
              Tell us what you'd want to work on and we'll keep it on file. We read everything that comes in.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleApply("Send a general application")}
              startIcon={<EmailIcon />}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                fontWeight: 700,
                px: 3,
                "&:hover": { borderColor: "primary.extraDark", backgroundColor: "primary.extraLight" },
              }}
            >
              Send a general application
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Bottom Spacer or Footer logic if needed (usually handled by layout) */}
      <Box sx={{ py: 4 }} />

      <SubmitResumeDialog
        open={dialogOpen}
        handleClose={() => setDialogOpen(false)}
        jobTitle={selectedJobTitle}
      />
    </Box>
  );
};

export default Careers;
